import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';
import { 
  ArrowLeft, FileText, Upload, Loader2, Download, Eye, 
  CheckCircle, Clock, XCircle, File, Image as ImageIcon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPES = [
  { value: 'photo', label: 'Passport Photo' },
  { value: 'aadhar', label: 'Aadhar Card' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'marksheet_10', label: '10th Marksheet' },
  { value: 'marksheet_12', label: '12th Marksheet' },
  { value: 'marksheet_ug', label: 'UG Marksheet' },
  { value: 'certificate_10', label: '10th Certificate' },
  { value: 'certificate_12', label: '12th Certificate' },
  { value: 'migration', label: 'Migration Certificate' },
  { value: 'tc', label: 'Transfer Certificate' },
  { value: 'caste', label: 'Caste Certificate' },
  { value: 'income', label: 'Income Certificate' },
  { value: 'domicile', label: 'Domicile Certificate' },
  { value: 'other', label: 'Other Document' }
];

export default function StudentDocuments() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
      return;
    }
    fetchDocuments();
  }, [navigate]);

  const fetchDocuments = async () => {
    const token = localStorage.getItem('studentToken');
    
    try {
      const response = await fetch(`${API_URL}/api/student-portal/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 200KB for documents)
    if (file.size > 200 * 1024) {
      toast.error('File size must be less than 200KB');
      return;
    }

    if (!selectedType) {
      toast.error('Please select document type first');
      return;
    }

    setUploading(true);

    try {
      const { readFileSmart } = await import('@/utils/imageCompressor');
      const result = await readFileSmart(file);
      const base64Data = result.data;
      if (result.compressed) {
        console.log(`Document compressed: ${(file.size/1024).toFixed(0)}KB → ${(result.size/1024).toFixed(0)}KB`);
      }

      const token = localStorage.getItem('studentToken');
      const response = await fetch(`${API_URL}/api/student-portal/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          document_type: selectedType,
          document_name: documentName || DOCUMENT_TYPES.find(d => d.value === selectedType)?.label,
          file_data: base64Data
        })
      });

      if (response.ok) {
        toast.success('Document uploaded successfully');
        setShowUploadModal(false);
        setSelectedType('');
        setDocumentName('');
        fetchDocuments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload document');
      }
      setUploading(false);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document');
      setUploading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return (
          <span className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded-full text-xs">
            <CheckCircle className="w-3 h-3" /> Verified
          </span>
        );
      case 'rejected':
        return (
          <span className="flex items-center gap-1 text-red-600 bg-red-50 px-2 py-1 rounded-full text-xs">
            <XCircle className="w-3 h-3" /> Rejected
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full text-xs">
            <Clock className="w-3 h-3" /> Pending
          </span>
        );
    }
  };

  const getFileIcon = (fileData) => {
    if (fileData?.startsWith('data:image/')) {
      return <ImageIcon className="w-8 h-8 text-blue-500" />;
    }
    return <File className="w-8 h-8 text-gray-500" />;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/student/profile')}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <h1 className="text-xl font-semibold">My Documents</h1>
            </div>
            <Button onClick={() => setShowUploadModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Upload Document</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Document Type *</label>
                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-1 block">Document Name (Optional)</label>
                  <Input
                    placeholder="e.g., My Aadhar Card"
                    value={documentName}
                    onChange={(e) => setDocumentName(e.target.value)}
                  />
                </div>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-2">Click to select file</p>
                  <p className="text-xs text-gray-400">Max size: 200KB (Images or PDF)</p>
                  <Button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!selectedType || uploading}
                    className="mt-4"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      'Select File'
                    )}
                  </Button>
                </div>
                
                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={() => setShowUploadModal(false)}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Preview Modal */}
        {previewDoc && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setPreviewDoc(null)}>
            <div className="max-w-4xl max-h-[90vh] overflow-auto bg-white rounded-lg p-4" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">{previewDoc.document_name}</h3>
                <Button variant="ghost" size="sm" onClick={() => setPreviewDoc(null)}>
                  <XCircle className="w-5 h-5" />
                </Button>
              </div>
              {previewDoc.file_data?.startsWith('data:image/') ? (
                <img src={previewDoc.file_data} alt={previewDoc.document_name} className="max-w-full" />
              ) : (
                <iframe src={previewDoc.file_data} className="w-full h-[600px]" title={previewDoc.document_name} />
              )}
            </div>
          </div>
        )}

        {/* Documents List */}
        {documents.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-2">No documents uploaded yet</p>
              <p className="text-sm text-gray-400">Upload your documents for verification</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {documents.map((doc) => (
              <Card key={doc.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                      {doc.file_data?.startsWith('data:image/') ? (
                        <img src={doc.file_data} alt="" className="w-full h-full object-cover" />
                      ) : (
                        getFileIcon(doc.file_data)
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{doc.document_name}</h3>
                      <p className="text-sm text-gray-500">
                        {DOCUMENT_TYPES.find(t => t.value === doc.document_type)?.label || doc.document_type}
                      </p>
                      <p className="text-xs text-gray-400">
                        Uploaded: {formatDate(doc.uploaded_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(doc.status)}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setPreviewDoc(doc)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
